import React from 'react';
import { Zap, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import type { ToolCall } from '../../types';

interface ToolCallDisplayProps {
  toolCall: ToolCall;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const ToolCallDisplay = React.memo(function ToolCallDisplay({ toolCall }: ToolCallDisplayProps) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="w-4 h-4 text-blue-600" aria-hidden="true" />
        <span className="font-semibold text-blue-900">{toolCall.name}</span>
        {toolCall.status === 'running' && (
          <Loader2 className="w-3 h-3 animate-spin text-blue-600" aria-hidden="true" />
        )}
        {toolCall.status === 'complete' && (
          <CheckCircle className="w-3 h-3 text-green-600" aria-hidden="true" />
        )}
        {toolCall.status === 'error' && (
          <AlertCircle className="w-3 h-3 text-red-600" aria-hidden="true" />
        )}
      </div>
      {toolCall.arguments && (
        <div className="bg-white rounded p-2 mb-2 font-mono text-xs">
          <details>
            <summary className="cursor-pointer text-gray-600">Arguments</summary>
            <pre className="mt-1 text-xs overflow-x-auto">{safeStringify(toolCall.arguments)}</pre>
          </details>
        </div>
      )}
      {toolCall.result != null && (
        <div className="bg-white rounded p-2 font-mono text-xs">
          <details>
            <summary className="cursor-pointer text-gray-600">Result</summary>
            <pre className="mt-1 text-xs overflow-x-auto">
              {typeof toolCall.result === 'string'
                ? toolCall.result
                : safeStringify(toolCall.result)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
});

export default ToolCallDisplay;
